import { useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { READONLY } from '../lib/readonly';
import { useData } from '../lib/data';
import { useSelection } from '../lib/selection';
import { useToast } from '../lib/toast';
import { updateTaskWithRollback } from '../lib/taskWrites';
import type { Task } from '../lib/database.types';

export function Completed() {
  const { tasks, projectBySlug, patchTaskLocal } = useData();
  const { select } = useSelection();
  const { push } = useToast();
  const [busyId, setBusyId] = useState<string | null>(null);

  const completed = useMemo(
    () =>
      tasks
        .filter((t) => t.status === 'completed' && t.completed_at)
        .sort((a, b) => (b.completed_at ?? '').localeCompare(a.completed_at ?? ''))
        .slice(0, 50),
    [tasks],
  );

  const reopen = async (task: Task) => {
    if (READONLY) return;
    setBusyId(task.id);
    await updateTaskWithRollback({
      task,
      patch: { status: 'pending', completed_at: null },
      patchTaskLocal,
      push,
      persist: async () => {
        const { data, error } = await supabase
          .from('tb_tasks')
          .update({ status: 'pending', completed_at: null })
          .eq('id', task.id)
          .select()
          .single();
        return { data: (data as Task | null) ?? null, error };
      },
      successToast: {
        kind: 'success',
        title: 'Task reopened',
        body: task.title.slice(0, 60),
        ttlMs: 3000,
      },
      errorTitle: 'Failed to reopen task',
    });
    setBusyId(null);
  };

  return (
    <div className="flex-1 overflow-auto">
      <div className="max-w-5xl p-8 space-y-6">
        <header>
          <h1 className="text-3xl font-semibold tracking-tight">Completed</h1>
          <p className="text-sm text-muted font-mono mt-1">
            last {Math.min(completed.length, 50)} completed task{completed.length === 1 ? '' : 's'}
          </p>
        </header>

        {completed.length === 0 && (
          <div className="panel p-10 text-center text-dim font-mono text-sm">
            no completed tasks yet
          </div>
        )}

        <div className="space-y-1.5">
          {completed.map((task) => {
            const project = projectBySlug[task.project_slug];
            return (
              <div
                key={task.id}
                className="panel p-3 flex items-center gap-3 hover:border-borderHi transition-colors"
              >
                <span className="text-green shrink-0">✓</span>
                <div className="flex-1 min-w-0">
                  <button
                    onClick={() => select({ type: 'task', id: task.id })}
                    className="text-sm font-medium text-left truncate line-through text-muted"
                  >
                    {task.title}
                  </button>
                  <div className="flex items-center gap-2 text-xs text-dim font-mono mt-0.5 flex-wrap">
                    <span>{project?.name ?? task.project_slug}</span>
                    <span>·</span>
                    <span>{task.id}</span>
                    <span>·</span>
                    <span>
                      {task.completed_at
                        ? new Date(task.completed_at).toLocaleString()
                        : 'completed'}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => reopen(task)}
                  disabled={busyId === task.id}
                  className="btn text-xs shrink-0"
                >
                  {busyId === task.id ? 'reopening…' : 'reopen'}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
